import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { adminBlogApi } from '../../lib/adminApi';
import type { ApiAdminBlogPost, BlogCategory } from '../../types/blog';
import { Button } from '../common/Button';
import { LoadingSpinner } from '../common/Loading';
import { ErrorMessage } from '../common/Error';
import { toast } from 'sonner';

const EMPTY_FORM = {
  title: '',
  excerpt: '',
  content: '',
  imageUrl: '',
  categoryId: '',
  isPublished: false,
};

export const AdminBlogPage: React.FC = () => {
  const [posts, setPosts] = useState<ApiAdminBlogPost[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [newCategory, setNewCategory] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [{ posts: list }, { categories: cats }] = await Promise.all([
        adminBlogApi.listPosts(),
        api.get<{ categories: BlogCategory[] }>('/blog/categories'),
      ]);
      setPosts(list);
      setCategories(cats);
    } catch (err) {
      console.error('AdminBlog: erro ao carregar', err);
      setError('Erro ao carregar posts do blog');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startEdit = (post: ApiAdminBlogPost) => {
    setEditingId(post.id);
    setForm({
      title: post.title,
      excerpt: post.excerpt,
      content: post.content,
      imageUrl: post.imageUrl,
      categoryId: post.categoryId ?? '',
      isPublished: post.isPublished,
    });
  };

  const startCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) {
      toast.error('Título e conteúdo são obrigatórios');
      return;
    }

    setSaving(true);
    const input = {
      title: form.title.trim(),
      content: form.content,
      excerpt: form.excerpt.trim() || undefined,
      imageUrl: form.imageUrl.trim() || undefined,
      categoryId: form.categoryId || undefined,
      isPublished: form.isPublished,
    };

    try {
      if (editingId) {
        await adminBlogApi.updatePost(editingId, input);
        toast.success('Post atualizado');
      } else {
        await adminBlogApi.createPost(input);
        toast.success('Post criado');
      }
      startCreate();
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao salvar o post');
      console.error('AdminBlog: save falhou', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (post: ApiAdminBlogPost) => {
    if (!window.confirm(`Deletar o post "${post.title}"? Não tem volta.`)) return;
    try {
      await adminBlogApi.deletePost(post.id);
      toast.success('Post deletado');
      if (editingId === post.id) startCreate();
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao deletar o post');
      console.error('AdminBlog: delete falhou', err);
    }
  };

  const handleCreateCategory = async () => {
    const name = newCategory.trim();
    if (!name) return;
    try {
      const { category } = await adminBlogApi.createCategory(name);
      setCategories(prev => (prev.some(c => c.id === category.id) ? prev : [...prev, category]));
      setForm(prev => ({ ...prev, categoryId: category.id }));
      setNewCategory('');
      toast.success(`Categoria "${category.name}" criada/reutilizada`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao criar categoria');
      console.error('AdminBlog: categoria falhou', err);
    }
  };

  return (
    <div className="space-y-8">
      <h2 className="text-2xl font-bold text-purple-200">Blog (admin)</h2>

      {loading ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : error ? (
        <>
          <ErrorMessage message={error} />
          <Button size="sm" variant="outline" onClick={() => load()}>Tentar de novo</Button>
        </>
      ) : (
        <>
          <form onSubmit={handleSave} className="bg-white/5 rounded-lg p-4 space-y-4">
            <h3 className="font-medium text-purple-200">
              {editingId ? 'Editar post' : 'Novo post'}
            </h3>

            <input
              value={form.title}
              onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Título *"
              className="input-primary"
              disabled={saving}
            />

            <input
              value={form.excerpt}
              onChange={(e) => setForm(prev => ({ ...prev, excerpt: e.target.value }))}
              placeholder="Resumo (excerpt)"
              className="input-primary"
              disabled={saving}
            />

            <textarea
              value={form.content}
              onChange={(e) => setForm(prev => ({ ...prev, content: e.target.value }))}
              placeholder="Conteúdo *"
              rows={8}
              className="input-primary"
              disabled={saving}
            />

            <input
              value={form.imageUrl}
              onChange={(e) => setForm(prev => ({ ...prev, imageUrl: e.target.value }))}
              placeholder="URL da imagem de capa"
              className="input-primary"
              disabled={saving}
            />

            <div className="flex flex-col md:flex-row gap-3 md:items-center">
              <select
                value={form.categoryId}
                onChange={(e) => setForm(prev => ({ ...prev, categoryId: e.target.value }))}
                className="input-primary md:w-64"
                disabled={saving}
              >
                <option value="">Sem categoria</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </select>

              <div className="flex gap-2 flex-1">
                <input
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  placeholder="Nova categoria…"
                  className="input-primary flex-1"
                  disabled={saving}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleCreateCategory}
                  disabled={saving || !newCategory.trim()}
                >
                  Criar categoria
                </Button>
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-purple-200">
              <input
                type="checkbox"
                checked={form.isPublished}
                onChange={(e) => setForm(prev => ({ ...prev, isPublished: e.target.checked }))}
                disabled={saving}
              />
              Publicado
            </label>

            <div className="flex gap-2">
              <Button type="submit" size="sm" loading={saving}>
                {editingId ? 'Salvar alterações' : 'Criar post'}
              </Button>
              {editingId && (
                <Button type="button" size="sm" variant="secondary" onClick={startCreate} disabled={saving}>
                  Cancelar edição
                </Button>
              )}
            </div>
          </form>

          <div className="space-y-3">
            {posts.length === 0 && (
              <p className="text-purple-200/70 py-4 text-center">Nenhum post ainda</p>
            )}
            {posts.map((post) => (
              <div key={post.id} className="bg-white/5 rounded-lg p-4 flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-purple-200 font-medium truncate">
                    {post.title}{' '}
                    <span className={`text-xs px-2 py-0.5 rounded-full ml-1 ${
                      post.isPublished
                        ? 'bg-green-500/20 text-green-300'
                        : 'bg-white/10 text-purple-200/70'
                    }`}>
                      {post.isPublished ? 'publicado' : 'rascunho'}
                    </span>
                  </p>
                  <p className="text-xs text-purple-200/50 truncate">
                    /{post.slug} · {categories.find(c => c.id === post.categoryId)?.name ?? 'sem categoria'}
                    {' · '}atualizado {new Date(post.updatedAt).toLocaleDateString('pt-BR')}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => startEdit(post)}>Editar</Button>
                  <Button size="sm" variant="secondary" onClick={() => handleDelete(post)}>Deletar</Button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};